import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { History, Loader2, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HistoryItem } from "@/components/history/HistoryItem";
import { API } from "@/lib/api";
import { getHistory, removeFromHistory } from "@/lib/lastJob";

const fetchJob = (id) => axios.get(`${API}/jobs/${id}`, { timeout: 15000 })
  .then((r) => ({ id, job: r.data }))
  .catch((e) => ({ id, gone: e.response?.status === 404 }));

export default function HistoryPage() {
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    const results = await Promise.all(getHistory().map((h) => fetchJob(h.id)));
    removeFromHistory(results.filter((r) => r.gone).map((r) => r.id));
    setItems(results.filter((r) => r.job));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const remove = (id) => {
    removeFromHistory([id]);
    setItems((list) => list.filter((it) => it.id !== id));
  };

  return (
    <main data-testid="history-page" className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
      <header className="mb-8 fade-up">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700"><History className="h-4 w-4" /> Ultima ora</p>
        <h1 className="mt-3 font-display text-4xl font-bold text-slate-900 sm:text-5xl">Storico conversioni</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-500 sm:text-base">Le conversioni avviate da questo browser, disponibili per circa un'ora dopo la fine. Clicca su Apri per tornare alla tabella.</p>
      </header>
      {!items && <div className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>}
      {items?.length === 0 && (
        <div data-testid="history-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 fade-up">
          <p className="font-display text-xl font-bold text-slate-900">Nessuna conversione nell'ultima ora</p>
          <p className="mt-2 text-sm text-slate-500">Quando converti degli estratti conto li ritrovi qui.</p>
          <Button asChild data-testid="history-upload-button" className="mt-6 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 btn-lift">
            <Link to="/"><UploadCloud className="mr-2 h-4 w-4" /> Vai al caricamento file</Link>
          </Button>
        </div>
      )}
      {items?.length > 0 && (
        <ul data-testid="history-list" className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white fade-up">
          {[...items].sort((a, b) => b.job.created - a.job.created).map((it) => <HistoryItem key={it.id} id={it.id} job={it.job} onRemove={remove} />)}
        </ul>
      )}
    </main>
  );
}
